(() => {
  'use strict';
  const SCHEMA = 'market-ref-catalog/v1';
  const MAX_BYTES = 2 * 1024 * 1024;
  const MAX_ITEMS = 20000;
  const NUMBERS = ['case_diameter_mm', 'case_thickness_mm', 'lug_width_mm'];
  const SORTS = ['ref_asc', 'ref_desc', 'brand', 'diameter_asc', 'diameter_desc'];
  const KEYS = ['q', 'brand', 'model', 'movement', 'case_material', ...NUMBERS.flatMap((key) => [`${key}_min`, `${key}_max`]), 'sort', 'page'];
  const cache = new Map();
  const collator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' });
  const text = (value, maximum = 512) => typeof value === 'string' && value.trim() && value.length <= maximum;
  const normalize = (value) => String(value ?? '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  const defaultState = () => Object.fromEntries(KEYS.map((key) => [key, key === 'sort' ? 'ref_asc' : key === 'page' ? 1 : '']));

  function catalogUrl(value, origin) {
    try {
      if (!text(value, 2048)) return null;
      const url = new URL(value, origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash
          || ![origin, 'https://cdn.shopify.com'].includes(url.origin) || !url.pathname.endsWith('.json')) return null;
      return url.href;
    } catch (_) { return null; }
  }

  function collectionUrl(value, origin, type, handle = null) {
    try {
      if (!text(value, 2048)) return null;
      const url = new URL(value, origin);
      if (url.origin !== origin || url.username || url.password || url.search || url.hash
          || !new RegExp(`^/collections/${type}-[a-z0-9]+(?:-[a-z0-9]+)*$`).test(url.pathname)) return null;
      if (handle !== null && url.pathname !== `/collections/${handle}`) return null;
      return url.pathname;
    } catch (_) { return null; }
  }

  function parent(value, origin, type) {
    if (value === null) return null;
    if (!value || !text(value.title) || !text(value.handle, 255)) throw Error('invalid_parent');
    const url = collectionUrl(value.canonical_url, origin, type, value.handle);
    if (!url) throw Error('invalid_parent_url');
    return { handle: value.handle, title: value.title, canonical_url: url };
  }

  function parseCatalog(data, origin, now = Date.now()) {
    try {
      if (data?.schema_version !== SCHEMA || data.content_kind !== 'reference_catalog'
          || data.refresh_policy !== 'on_catalog_change' || data.expires_at !== null
          || !text(data.snapshot_id, 200) || !/^\d{4}-\d{2}-\d{2}$/.test(data.as_of)
          || !Number.isFinite(Date.parse(`${data.as_of}T00:00:00Z`))
          || new Date(`${data.as_of}T00:00:00Z`).toISOString().slice(0, 10) !== data.as_of
          || !Array.isArray(data.items) || data.items.length > MAX_ITEMS || data.item_count !== data.items.length) return null;
      for (const key of ['generated_at', 'links_verified_at', 'snapshot_generated_at']) {
        if (!text(data[key], 50) || !Number.isFinite(Date.parse(data[key])) || Date.parse(data[key]) > now + 300000) return null;
      }
      const ids = new Set(); const urls = new Set(); const items = [];
      for (const raw of data.items) {
        const url = collectionUrl(raw?.canonical_url, origin, 'ref');
        if (!url || !text(raw.reference_id, 200) || !text(raw.canonical_ref, 200)
            || ids.has(raw.reference_id) || urls.has(url)) return null;
        const brand = parent(raw.brand, origin, 'brand');
        const model = parent(raw.model, origin, 'model');
        const expected = brand ? model ? 'verified' : 'brand_only' : 'unavailable';
        if (model && !brand || raw.classification_status !== expected) return null;
        const specs = {};
        for (const key of ['movement', 'case_material', ...NUMBERS]) {
          const value = raw.specifications?.[key];
          if (value !== null && (NUMBERS.includes(key) ? typeof value !== 'number' || !Number.isFinite(value) || value <= 0 : !text(value, 200))) return null;
          specs[key] = value;
        }
        ids.add(raw.reference_id); urls.add(url);
        items.push({ reference_id: raw.reference_id, canonical_ref: raw.canonical_ref, canonical_url: url,
          brand, model, classification_status: expected, specifications: specs,
          searchable: [raw.canonical_ref, brand?.title, model?.title].filter(Boolean).map(normalize).join(' ') });
      }
      return { items, snapshotId: data.snapshot_id };
    } catch (_) { return null; }
  }

  function readState(href, prefix) {
    const state = defaultState();
    const params = new URL(href).searchParams;
    for (const key of KEYS) {
      const raw = params.get(`${prefix}${key}`);
      if (raw !== null && raw.length <= 200) state[key] = raw.trim();
    }
    state.page = /^\d+$/.test(String(state.page)) ? Math.max(1, Math.min(20000, Number(state.page))) : 1;
    if (!SORTS.includes(state.sort)) state.sort = 'ref_asc';
    return state;
  }

  function writeState(href, prefix, state) {
    const url = new URL(href); const defaults = defaultState();
    for (const key of KEYS) {
      const value = state[key] ?? defaults[key];
      if (value === '' || String(value) === String(defaults[key])) url.searchParams.delete(`${prefix}${key}`);
      else url.searchParams.set(`${prefix}${key}`, String(value));
    }
    return url.href;
  }

  function validRanges(state) {
    return NUMBERS.every((key) => {
      const min = state[`${key}_min`]; const max = state[`${key}_max`];
      const valid = (value) => value === '' || /^(?:\d+\.?\d*|\.\d+)$/.test(String(value)) && Number.isFinite(Number(value)) && Number(value) >= 0;
      return valid(min) && valid(max) && (min === '' || max === '' || Number(min) <= Number(max));
    });
  }

  function select(items, state, pageSize = 24) {
    const tokens = String(state.q ?? '').normalize('NFKC').trim().split(/\s+/u).map(normalize).filter(Boolean);
    const matched = validRanges(state) ? items.filter((item) => {
      if (tokens.some((token) => !item.searchable.includes(token))) return false;
      if (state.brand && item.brand?.handle !== state.brand || state.model && item.model?.handle !== state.model) return false;
      if (state.movement && item.specifications.movement !== state.movement || state.case_material && item.specifications.case_material !== state.case_material) return false;
      return NUMBERS.every((key) => {
        const min = state[`${key}_min`]; const max = state[`${key}_max`]; const value = item.specifications[key];
        return min === '' && max === '' || value !== null && (min === '' || value >= Number(min)) && (max === '' || value <= Number(max));
      });
    }) : [];
    matched.sort((a, b) => {
      let order = 0;
      if (state.sort === 'brand') order = collator.compare(a.brand?.title ?? '\uffff', b.brand?.title ?? '\uffff');
      else if (state.sort.startsWith('diameter_')) {
        const av = a.specifications.case_diameter_mm; const bv = b.specifications.case_diameter_mm;
        order = av === null ? bv === null ? 0 : 1 : bv === null ? -1 : (av - bv) * (state.sort === 'diameter_desc' ? -1 : 1);
      }
      if (!order) order = collator.compare(a.canonical_ref, b.canonical_ref) * (state.sort === 'ref_desc' ? -1 : 1);
      return order || collator.compare(a.reference_id, b.reference_id);
    });
    const size = Number.isInteger(pageSize) && pageSize >= 1 && pageSize <= 60 ? pageSize : 24;
    const pages = Math.max(1, Math.ceil(matched.length / size));
    const page = Math.max(1, Math.min(pages, Number.isInteger(state.page) ? state.page : 1));
    return { total: matched.length, page, pages, items: matched.slice((page - 1) * size, page * size) };
  }

  function options(items, key, brand = '') {
    const values = new Map();
    for (const item of items) {
      if (key === 'model' && brand && item.brand?.handle !== brand) continue;
      const source = key === 'brand' || key === 'model' ? item[key] : item.specifications[key];
      if (source !== null) values.set(typeof source === 'string' ? source : source.handle, typeof source === 'string' ? source : source.title);
    }
    return [...values].sort((a, b) => collator.compare(a[1], b[1]) || collator.compare(a[0], b[0]));
  }

  async function load(url, origin) {
    if (cache.has(url)) return cache.get(url);
    const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 10000);
    const request = fetch(url, { signal: controller.signal, credentials: 'omit', redirect: 'error', headers: { Accept: 'application/json' } })
      .then(async (response) => {
        if (!response.ok || Number(response.headers.get('content-length')) > MAX_BYTES) return null;
        let raw = '';
        if (response.body?.getReader) {
          const reader = response.body.getReader(); const decoder = new TextDecoder(); let bytes = 0;
          while (true) {
            const part = await reader.read();
            if (part.done) { raw += decoder.decode(); break; }
            bytes += part.value.byteLength;
            if (bytes > MAX_BYTES) { await reader.cancel(); return null; }
            raw += decoder.decode(part.value, { stream: true });
          }
        } else {
          raw = await response.text();
          if (new TextEncoder().encode(raw).byteLength > MAX_BYTES) return null;
        }
        return parseCatalog(JSON.parse(raw), origin);
      }).catch(() => null).finally(() => clearTimeout(timer));
    cache.set(url, request); return request;
  }

  class Catalog {
    constructor(root) {
      this.root = root; this.form = root.querySelector('form'); this.status = root.querySelector('[data-catalog-status]');
      this.results = root.querySelector('[data-catalog-results]'); this.list = root.querySelector('[data-catalog-list]');
      this.paging = root.querySelector('[data-catalog-pagination]'); this.previous = root.querySelector('[data-catalog-previous]');
      this.next = root.querySelector('[data-catalog-next]'); this.pageLabel = root.querySelector('[data-catalog-page-label]');
      this.prefix = `tscat_${root.dataset.stateKey}_`; this.state = defaultState();
      this.origin = window.location.origin;
      this.popstate = () => { this.state = readState(window.location.href, this.prefix); this.syncForm(); this.render(); };
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.apply(); });
      this.form.addEventListener('change', (event) => {
        if (event.target.name === 'brand') this.fillOptions('model', options(this.items, 'model', event.target.value), '');
      });
      root.querySelector('[data-catalog-reset]').addEventListener('click', () => { this.state = defaultState(); this.syncForm(); this.render(true); });
      this.previous.addEventListener('click', () => this.move(-1)); this.next.addEventListener('click', () => this.move(1));
    }
    field(key) { return this.form.elements.namedItem(key); }
    fillOptions(key, values, selected) {
      const select = this.field(key); select.replaceChildren();
      for (const [value, label] of [['', this.root.dataset.allLabel], ...values]) {
        const option = document.createElement('option'); option.value = value; option.textContent = label; select.appendChild(option);
      }
      if (selected && !values.some(([value]) => value === selected)) {
        const option = document.createElement('option'); option.value = selected; option.textContent = selected; select.appendChild(option);
      }
      select.value = selected;
    }
    syncForm() {
      for (const key of ['brand', 'movement', 'case_material']) this.fillOptions(key, options(this.items, key), this.state[key]);
      this.fillOptions('model', options(this.items, 'model', this.state.brand), this.state.model);
      for (const key of KEYS.filter((key) => !['brand', 'model', 'movement', 'case_material', 'page'].includes(key))) this.field(key).value = this.state[key];
      this.form.querySelector('details').open = ['movement', 'case_material', ...NUMBERS.flatMap((key) => [`${key}_min`, `${key}_max`])].some((key) => this.state[key] !== '');
    }
    async start() {
      const url = catalogUrl(this.root.dataset.catalogUrl, this.origin);
      const catalog = url ? await load(url, this.origin) : null;
      if (this.disposed) return;
      if (!catalog) { this.status.textContent = this.root.dataset.errorLabel; this.root.dataset.state = 'error'; return; }
      this.items = catalog.items;
      if (this.root.dataset.scopeHandle) {
        const handle = this.root.dataset.scopeHandle; const type = this.root.dataset.scopeType;
        this.items = this.items.filter((item) => type === 'brand' ? item.brand?.handle === handle : type === 'model' ? item.model?.handle === handle : item.canonical_url === `/collections/${handle}`);
      }
      this.state = readState(window.location.href, this.prefix); this.syncForm(); this.render();
      this.form.hidden = false; this.results.hidden = false; this.root.dataset.state = 'ready';
      window.addEventListener('popstate', this.popstate);
    }
    apply() {
      for (const key of KEYS.filter((key) => key !== 'page')) this.state[key] = this.field(key).value.trim();
      this.state.page = 1;
      this.render(true);
    }
    move(direction) { this.state.page += direction; this.render(true); this.results.focus({ preventScroll: true }); }
    render(updateUrl = false) {
      const result = select(this.items, this.state, Number(this.root.dataset.pageSize)); this.state.page = result.page;
      this.status.textContent = !validRanges(this.state) ? this.root.dataset.rangeErrorLabel : result.total ? this.root.dataset.countLabel.replace('{count}', String(result.total)) : this.root.dataset.emptyLabel;
      this.list.replaceChildren();
      for (const item of result.items) {
        const row = document.createElement('li'); row.className = 'ts-ref-catalog__item';
        const title = document.createElement('h3'); const link = document.createElement('a');
        link.href = item.canonical_url; link.textContent = item.canonical_ref; title.appendChild(link); row.appendChild(title);
        for (const type of ['brand', 'model']) if (item[type]) {
          const line = document.createElement('p'); line.className = 'ts-ref-catalog__parent'; const parentLink = document.createElement('a');
          parentLink.href = item[type].canonical_url; parentLink.textContent = item[type].title; line.appendChild(parentLink); row.appendChild(line);
        }
        const specs = document.createElement('dl'); specs.className = 'ts-ref-catalog__specs';
        for (const key of ['movement', 'case_material', ...NUMBERS]) if (item.specifications[key] !== null) {
          const pair = document.createElement('div'); const term = document.createElement('dt'); const detail = document.createElement('dd');
          term.textContent = this.root.querySelector(`[data-spec-label="${key}"]`).textContent;
          detail.textContent = String(item.specifications[key]); pair.append(term, detail); specs.appendChild(pair);
        }
        if (specs.children.length) row.appendChild(specs);
        this.list.appendChild(row);
      }
      this.paging.hidden = result.pages === 1; this.previous.disabled = result.page === 1; this.next.disabled = result.page === result.pages;
      this.pageLabel.textContent = this.root.dataset.pageLabel.replace('{page}', String(result.page)).replace('{pages}', String(result.pages));
      if (updateUrl) {
        const href = writeState(window.location.href, this.prefix, this.state);
        if (href !== window.location.href) window.history.pushState(window.history.state, '', href);
      }
    }
    destroy() { this.disposed = true; window.removeEventListener('popstate', this.popstate); }
  }

  function mount(scope = document) {
    for (const root of scope.querySelectorAll('[data-ts-ref-catalog]')) {
      if (root.tsRefCatalog) continue;
      root.tsRefCatalog = new Catalog(root); root.tsRefCatalog.start();
    }
  }
  window.TimeseekRefCatalog = { parseCatalog, catalogUrl, collectionUrl, readState, writeState, validRanges, select, options, defaultState, Catalog, load };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount()); else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => { for (const root of event.target.querySelectorAll('[data-ts-ref-catalog]')) root.tsRefCatalog?.destroy(); });
})();
